<template>
  <div class="welcome-overlay">
    <div v-if="step === 'greeting'" class="welcome-step" @click="step = 'name'">
      <div class="wave-emoji">👋</div>
      <h1 class="greeting-title">Привет!</h1>
      <p class="greeting-sub">Давай познакомимся</p>
      <div class="tap-hint">нажми, чтобы продолжить</div>
    </div>

    <div v-if="step === 'name'" class="welcome-step">
      <div class="step-emoji">😊</div>
      <h2 class="step-title">Как тебя зовут?</h2>
      <form class="name-form" @submit.prevent="submitName">
        <input
          ref="nameInput"
          v-model="name"
          type="text"
          class="name-input"
          placeholder="Введи своё имя"
          autocomplete="off"
          maxlength="30"
        />
        <button type="submit" class="btn-next" :disabled="!name.trim()">Далее</button>
      </form>
    </div>

    <div v-if="step === 'photo'" class="welcome-step">
      <div class="step-emoji">📸</div>
      <h2 class="step-title">Сделай фото на аватар!</h2>

      <div v-if="!cameraReady" class="camera-loading">
        <p>Загружаем камеру...</p>
      </div>

      <div v-if="!captured" class="camera-area">
        <video ref="videoEl" autoplay playsinline muted class="camera-video"></video>
        <button class="btn-capture" :disabled="!cameraReady" @click="capturePhoto">
          <span class="capture-ring"></span>
        </button>
      </div>

      <div v-else class="preview-area">
        <img :src="avatarData" class="avatar-preview" />
        <div class="preview-actions">
          <button class="btn-retake" @click="retakePhoto">Переснять</button>
          <button class="btn-next" @click="finish">Готово!</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useUserProfile } from '../composables/useUserProfile.js'

const router = useRouter()
const { saveName, saveAvatar } = useUserProfile()

const step = ref('greeting')
const name = ref('')
const nameInput = ref(null)
const videoEl = ref(null)
const cameraReady = ref(false)
const captured = ref(false)
const avatarData = ref('')

let stream = null

async function submitName() {
  if (!name.value.trim()) return
  saveName(name.value.trim())
  step.value = 'photo'
  await nextTick()
  startCamera()
}

async function startCamera() {
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 640 } },
      audio: false,
    })
    if (videoEl.value) {
      videoEl.value.srcObject = stream
      await videoEl.value.play()
      cameraReady.value = true
    }
  } catch {
    cameraReady.value = true
  }
}

function capturePhoto() {
  const video = videoEl.value
  if (!video) return

  const size = Math.min(video.videoWidth, video.videoHeight)
  const canvas = document.createElement('canvas')
  canvas.width = 320
  canvas.height = 320
  const ctx = canvas.getContext('2d')

  const sx = (video.videoWidth - size) / 2
  const sy = (video.videoHeight - size) / 2
  ctx.drawImage(video, sx, sy, size, size, 0, 0, 320, 320)

  avatarData.value = canvas.toDataURL('image/jpeg', 0.8)
  captured.value = true

  if (stream) {
    stream.getTracks().forEach(t => t.stop())
    stream = null
  }
}

function retakePhoto() {
  captured.value = false
  avatarData.value = ''
  startCamera()
}

function finish() {
  saveAvatar(avatarData.value)
  router.replace('/')
}

onMounted(() => {
  if (step.value === 'name') {
    nextTick(() => nameInput.value?.focus())
  }
})
</script>

<style scoped>
.welcome-overlay {
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
}

.welcome-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: white;
  max-width: 400px;
  width: 100%;
  animation: fadeUp 0.5s ease;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.wave-emoji {
  font-size: 80px;
  animation: wave 1s ease infinite alternate;
  cursor: pointer;
}

@keyframes wave {
  from { transform: rotate(-10deg); }
  to { transform: rotate(20deg); }
}

.greeting-title {
  font-size: 48px;
  font-weight: 900;
  margin-top: 20px;
}

.greeting-sub {
  font-size: 22px;
  opacity: 0.9;
  margin-top: 12px;
}

.tap-hint {
  margin-top: 40px;
  font-size: 14px;
  opacity: 0.5;
  animation: pulse 2s ease infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.7; }
}

.step-emoji {
  font-size: 60px;
  margin-bottom: 16px;
}

.step-title {
  font-size: 28px;
  font-weight: 900;
  margin-bottom: 24px;
}

.name-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}

.name-input {
  width: 100%;
  max-width: 300px;
  padding: 16px 20px;
  border: none;
  border-radius: 16px;
  font-size: 20px;
  font-family: 'Nunito', sans-serif;
  font-weight: 700;
  text-align: center;
  outline: none;
  background: rgba(255, 255, 255, 0.95);
  color: #2c3e50;
}

.name-input::placeholder {
  color: #bbb;
  font-weight: 400;
}

.btn-next {
  padding: 14px 48px;
  background: white;
  color: #764ba2;
  border: none;
  border-radius: 16px;
  font-size: 20px;
  font-weight: 900;
  font-family: 'Nunito', sans-serif;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.btn-next:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.25);
}

.btn-next:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.camera-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
}

.camera-video {
  width: 280px;
  height: 280px;
  border-radius: 50%;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.1);
  border: 4px solid rgba(255, 255, 255, 0.3);
}

.btn-capture {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: 4px solid white;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s;
}

.btn-capture:hover {
  transform: scale(1.1);
}

.btn-capture:disabled {
  opacity: 0.3;
  cursor: not-allowed;
  transform: none;
}

.capture-ring {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: white;
  display: block;
}

.camera-loading {
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  padding: 60px 0;
}

.preview-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.avatar-preview {
  width: 280px;
  height: 280px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid rgba(255, 255, 255, 0.5);
}

.preview-actions {
  display: flex;
  gap: 16px;
}

.btn-retake {
  padding: 14px 32px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  font-size: 18px;
  font-weight: 700;
  font-family: 'Nunito', sans-serif;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-retake:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
